/**
 * Azimuthal wrap of a shaft discontinuity trace into the log frame.
 *
 * The log frame spans one full revolution of the shaft wall: x in
 * [padding, padding + frameW], where frameW = 4 * Arc = 2 * PI * R(px).
 * A trace is generated centred on the view direction D; to place it at its
 * own dip direction Dir it must be shifted by -toMove, with
 * toMove = ((D - Dir) * frameW) / 360, wrapping around the frame edges.
 */

/**
 * Map a single x coordinate to its wrapped position inside the frame.
 */
export const wrapTraceX = (x, padding, frameW, toMove) => {
  let rel = (x - padding - toMove) % frameW;
  if (rel < 0) rel += frameW;
  return padding + rel;
};

/**
 * Wrap a polyline of [x, y] points into the frame, splitting it into
 * separate runs wherever the wrap produces a horizontal jump, so no
 * spurious connector is drawn across the log. Single-point runs (a lone
 * sample stranded at a seam) are dropped — they have no drawable extent.
 *
 * Returns an array of runs, each an array of [x, y] points with length >= 2.
 */
export const wrapTraceIntoRuns = (points, padding, frameW, toMove) => {
  if (!points.length) return [];
  const wrapped = points.map(([x, y]) => [
    wrapTraceX(x, padding, frameW, toMove),
    y,
  ]);
  const runs = [];
  let run = [wrapped[0]];
  for (let n = 1; n < wrapped.length; n++) {
    if (Math.abs(wrapped[n][0] - wrapped[n - 1][0]) > frameW / 2) {
      runs.push(run);
      run = [];
    }
    run.push(wrapped[n]);
  }
  runs.push(run);
  return runs.filter((r) => r.length > 1);
};
